<template>
  <div>
    <div class="header profile" />
    <div class="container">
      <div class="box is-large gutter">
        <h1 class="title is-1">
          Your cart
        </h1>

        <div v-if="$store.getters['cart/cartCount'] > 0">
          <div
            v-for="(item, i) in $store.getters['cart/cart']"
            :key="`${i}-${item.track.pk}-${item.product.pk}`"
            class="columns is-mobile is-vcentered track"
          >
            <div
              class="column cover is-narrow is-hidden-mobile"
            >
              <img :src="item.track.cover_thumbnail">
            </div>
            <div class="column">
              <p class="text label-medium track-title">
                {{ item.track.display_title }}
              </p>
              <p class="text small track-artist">
                {{ item.track.artist.name }}
              </p>
              <p
                v-if="item.track.instrumental_version"
                class="text small"
              >
                <span
                  class="icon-static small info"
                  style="margin: -4px 0 -4px 0;"
                />
                Includes instrumental version
              </p>
              <span class="tag is-small is-hidden-tablet">{{ item.product.name }}</span>
            </div>
            <div class="column is-hidden-mobile">
              <h3 class="title is-4">
                {{ item.product.name }} Licence
              </h3>
            </div>
            <div class="column is-narrow">
              {{ item.product.amount|formattedDbAmount(item.product.currency.symbol) }}
            </div>
            <div class="column is-narrow">
              <a
                class="icon-button trash"
                @click="removeFromCart(item)"
              />
            </div>
          </div>
          <p
            v-if="$store.getters['cart/hasMixedCurrencies']"
            class="help is-danger"
            role="alert"
          >
            You have products with multiple currencies in your cart. Please clear your cart and start again.
          </p>
          <div class="total">
            <h3 class="title is-3 is-pulled-left">
              Total
            </h3>
            <span class="is-pulled-right text caption">{{ $store.getters['cart/total']|formattedDbAmount(currencySymbol) }}</span>
          </div>
        </div>
        <b-notification
          v-else
          :closable="false"
        >
          Your cart is empty.
        </b-notification>

        <div class="footer">
          <button
            :disabled="!canCheckout"
            class="button is-primary is-large is-pulled-right"
            @click.prevent="handleGoToCheckoutClick"
          >
            Go to checkout
          </button>
        </div>
      </div>
    </div>

    <b-modal
      :active.sync="showSubscriptionPromoModal"
      class="is-full-screen-modal"
      full-screen
    >
      <div
        id="pricing"
        class="modal-card pricing-page"
        style="width: auto;"
      >
        <header
          class="modal-card-head is-block "
          style="padding: 28px 32px 8px 32px;"
        >
          <span class="icon-static xl large-cart margin-bottom-lg" />
          <h3 class="title is-3">
            Download unlimited tracks with a subscription
          </h3>
        </header>
        <section
          class="modal-card-body"
          style="padding: 0 36px 28px 36px;"
        >
          <div class="container">
            <div
              class="columns mb-6"
            >
              <div class="column" />
              <div class="column is-9-tablet is-11-desktop is-9-widescreen">
                <div class="mb-4">
                  <p class="text large">
                    You are purchasing a single-use license for a single track. If you opt for a
                    subscription you'll have access to unlimited downloads from our catalogue.
                    See full plan details and FAQs on our
                    <router-link :to="{ name: 'pricing' }">pricing page</router-link>.
                  </p>
                </div>

                <div>
                  <b-switch
                    v-model="showYearly"
                    class="ml-0 is-left-right is-accent"
                    type="is-danger"
                  >
                    {{ intervalSwitchText }}
                  </b-switch>
                </div>
                <div class="is-clearfix" />

                <div
                  id="table"
                  class="columns mt-5 mb-5 ml-1"
                >
                  <div class="column">
                    <pricing-plan-card-list
                      class="text"
                      :plans="sortedPlans"
                      compact
                    />
                  </div>
                </div>

                <router-link
                  :to="{ name: 'checkout' }"
                  class="button is-primary is-large is-pulled-right"
                >
                  Checkout without subscription
                </router-link>
              </div>
              <div class="column" />
            </div>
          </div>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import PricingPlanCardList from '@/components/PricingPlanCardList.vue'

export default {
  name: 'ShoppingCart',

  components: {
    PricingPlanCardList
  },

  data () {
    return {
      showSubscriptionPromoModal: false,
      showYearly: false
    }
  },

  computed: {
    user: function () {
      return this.$store.getters['user/currentUser']
    },

    fullName: function () {
      return this.$store.getters['user/fullName']
    },

    currencySymbol: function () {
      return this.$store.getters['cart/currencySymbol']
    },

    canCheckout: function () {
      return this.$store.getters['cart/cartCount'] > 0 && !this.$store.getters['cart/hasMixedCurrencies']
    },

    sortedPlansMonthly: function () {
      const plans = [...this.$store.getters['subscriptions/plans']].filter((plan) => plan.interval === 'month')
      return plans.sort((a, b) => a.amount - b.amount)
    },

    sortedPlansYearly: function () {
      const plans = [...this.$store.getters['subscriptions/plans']].filter((plan) => plan.interval === 'year')
      return plans.sort((a, b) => a.amount - b.amount)
    },

    sortedPlans: function () {
      if (this.showYearly) {
        return this.sortedPlansYearly
      } else {
        return this.sortedPlansMonthly
      }
    },

    intervalSwitchText: function () {
      if (this.showYearly) {
        return 'Show monthly'
      } else {
        return 'Get two months free when paid yearly'
      }
    }
  },

  methods: {
    removeFromCart: function (item) {
      this.$store.dispatch('cart/removeItemFromCart', item)
    },

    handleGoToCheckoutClick: function () {
      const currentSubscription = this.$store.getters['subscriptions/currentSubscription']
      if (!currentSubscription) {
        // Show modal promoting subscriptions
        this.showSubscriptionPromoModal = true
      } else {
        this.$router.push({ name: 'checkout' })
      }
    }
  },

  metaInfo: {
    title: 'Cart'
  }
}

</script>

<style scoped lang="scss">
@import "../assets/scss/_variables.scss";

.total {
  margin: 16px 48px 24px 0;
  clear: both;
  overflow: hidden;
}
.track {
  @media only screen and (max-width: $tablet) {
    height: 100px;
  }
}
</style>
